/**
 * Keep Horizon cart drawer in sync for non-theme ATC paths
 * (drawer upsell form, Zepto, other apps that POST /cart/add then redirect to /cart).
 */
(function () {
  const CART_PATH = /^\/cart\/?$/;
  const CART_ADD_PATH = /\/cart\/add(\.js)?$/;

  let suppressCartRedirectUntil = 0;

  function shouldSuppressCartRedirect() {
    return Date.now() < suppressCartRedirectUntil;
  }

  function markCartAddSuccess() {
    suppressCartRedirectUntil = Date.now() + 4000;
  }

  function isCartUrl(url) {
    try {
      const parsed = new URL(String(url), window.location.origin);
      return CART_PATH.test(parsed.pathname);
    } catch {
      return false;
    }
  }

  function isCartAddUrl(url) {
    try {
      const parsed = new URL(String(url), window.location.origin);
      return CART_ADD_PATH.test(parsed.pathname);
    } catch {
      return typeof url === 'string' && /\/cart\/add/.test(url);
    }
  }

  function getCartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean)
      .join(',');
  }

  function openCartDrawer() {
    const drawer = document.getElementById('site-cart-drawer');
    if (!drawer) return;

    if (typeof drawer.open === 'function') {
      drawer.open();
      return;
    }

    if (typeof drawer.showDialog === 'function') {
      drawer.showDialog();
    }
  }

  async function fetchCart() {
    try {
      const response = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return null;
      return response.json();
    } catch {
      return null;
    }
  }

  async function notifyCartUpdated(sections) {
    markCartAddSuccess();

    const cart = await fetchCart();

    try {
      const { CartAddEvent } = await import('@theme/events');
      document.dispatchEvent(
        new CartAddEvent(cart ?? {}, 'fcd-cart-drawer-bridge', {
          source: 'fcd-cart-drawer-bridge',
          itemCount: cart?.item_count,
          sections,
        })
      );
    } catch {
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart ?? {},
            sourceId: 'fcd-cart-drawer-bridge',
            data: {
              source: 'fcd-cart-drawer-bridge',
              sections,
              itemCount: cart?.item_count,
            },
          },
        })
      );
    }

    openCartDrawer();
  }

  async function addFormToCart(form) {
    const formData = new FormData(form);
    const sections = getCartSectionIds();
    if (sections) formData.append('sections', sections);

    const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: formData,
    });

    let data = null;
    try {
      data = await response.json();
    } catch {
      throw new Error('Invalid cart response');
    }

    if (!response.ok || data?.status) {
      throw new Error(data?.description || data?.message || 'Could not add to cart');
    }

    await notifyCartUpdated(data.sections);
    return data;
  }

  // Native forms not handled by product-form-component (e.g. drawer upsell).
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.closest('product-form-component')) return;
      if (!isCartAddUrl(form.action || form.getAttribute('action') || '')) return;

      event.preventDefault();
      event.stopPropagation();

      const submitButton = form.querySelector('[type="submit"]');
      if (submitButton instanceof HTMLButtonElement) submitButton.disabled = true;

      addFormToCart(form)
        .catch((error) => {
          console.error('[fcd-cart-drawer-bridge]', error);
          window.alert(error instanceof Error ? error.message : 'Could not add to cart');
        })
        .finally(() => {
          if (submitButton instanceof HTMLButtonElement) submitButton.disabled = false;
        });
    },
    true
  );

  // Detect AJAX cart/add from apps. If they didn't request theme sections, refresh the drawer ourselves.
  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const response = await nativeFetch(input, init);

    if (!isCartAddUrl(url) || !response.ok) return response;

    try {
      const data = await response.clone().json();
      if (data?.status) return response;

      markCartAddSuccess();

      if (!data.sections) {
        notifyCartUpdated(undefined);
      } else {
        // Theme ATC already morphs via CartAddEvent; still open drawer as a safety net.
        window.setTimeout(openCartDrawer, 150);
      }
    } catch {
      // Ignore non-JSON cart/add responses.
    }

    return response;
  };

  const originalAssign = window.location.assign.bind(window.location);
  const originalReplace = window.location.replace.bind(window.location);

  window.location.assign = (url) => {
    if (shouldSuppressCartRedirect() && isCartUrl(url)) {
      openCartDrawer();
      return;
    }
    return originalAssign(url);
  };

  window.location.replace = (url) => {
    if (shouldSuppressCartRedirect() && isCartUrl(url)) {
      openCartDrawer();
      return;
    }
    return originalReplace(url);
  };

  // Zepto / jQuery often set location.href = '/cart'
  try {
    const descriptor = Object.getOwnPropertyDescriptor(Location.prototype, 'href');
    if (descriptor?.set && descriptor?.get) {
      Object.defineProperty(window.location, 'href', {
        configurable: true,
        get() {
          return descriptor.get.call(window.location);
        },
        set(value) {
          if (shouldSuppressCartRedirect() && isCartUrl(value)) {
            openCartDrawer();
            return;
          }
          descriptor.set.call(window.location, value);
        },
      });
    }
  } catch {
    // Some browsers disallow redefining location.href.
  }
})();
